import { useCallback, useEffect, useRef, useState } from 'react';
import { getAutoplaysettings } from '../utils/autoplaysettings';

function normalizeAudioUrl(url) {
  if (!url) return '';
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return String(url);
  }
}

function audioMatchesElement(el, expectedUrl) {
  if (!el || !expectedUrl) return false;
  const expected = normalizeAudioUrl(expectedUrl);
  const actual = normalizeAudioUrl(el.currentSrc || el.src || '');
  return actual === expected;
}

/**
 * Wait for the clip at `expectedUrl` to finish, then call `fireAction`.
 * Avoids treating a stale `ended` state from the previous scene as complete.
 */
function scheduleAfterAudio({
  audioEl,
  expectedUrl,
  textLength,
  fireAction,
  pauseMs,
  sceneTimerRef,
  audioCleanupRef,
}) {
  const cushionMs = 1000 + pauseMs;
  const textFallbackMs = Math.max(5000, textLength * 100);

  const clearWaiters = () => {
    if (audioCleanupRef.current) {
      audioCleanupRef.current();
      audioCleanupRef.current = null;
    }
  };

  const scheduleFallback = () => {
    sceneTimerRef.current = setTimeout(fireAction, textFallbackMs);
  };

  const armEndedListener = () => {
    if (!audioMatchesElement(audioEl, expectedUrl)) return false;

    clearTimeout(sceneTimerRef.current);
    sceneTimerRef.current = null;

    if (audioEl.ended) {
      sceneTimerRef.current = setTimeout(fireAction, cushionMs);
      return true;
    }

    const handleEnded = () => {
      if (!audioMatchesElement(audioEl, expectedUrl)) return;
      sceneTimerRef.current = setTimeout(fireAction, cushionMs);
    };

    audioEl.addEventListener('ended', handleEnded, { once: true });
    audioCleanupRef.current = () => audioEl.removeEventListener('ended', handleEnded);
    return true;
  };

  clearWaiters();

  if (armEndedListener()) return;

  const waitEvents = ['loadeddata', 'canplay', 'play'];
  const waitHandlers = waitEvents.map((eventName) => {
    const handler = () => {
      if (!audioMatchesElement(audioEl, expectedUrl)) return;
      waitHandlers.forEach(({ eventName: evt, handler: fn }) => {
        audioEl.removeEventListener(evt, fn);
      });
      if (!armEndedListener()) scheduleFallback();
    };
    audioEl.addEventListener(eventName, handler);
    return { eventName, handler };
  });

  audioCleanupRef.current = () => {
    waitHandlers.forEach(({ eventName, handler }) => {
      audioEl.removeEventListener(eventName, handler);
    });
  };

  // If the clip never loads (blocked autoplay, network error), fall back to text pacing.
  sceneTimerRef.current = setTimeout(() => {
    if (!audioMatchesElement(audioEl, expectedUrl)) {
      clearWaiters();
      scheduleFallback();
      return;
    }
    if (!audioEl.ended && !audioEl.paused) return;
    if (audioEl.ended) {
      clearWaiters();
      sceneTimerRef.current = setTimeout(fireAction, cushionMs);
    }
  }, textFallbackMs);
}

/**
 * Drives scene-level autoplay for the story player.
 */
export default function useAutoplay({
  currentIndex,
  totalScenes,
  currentAudioUrl,
  currentTextLength,
  storyId,    // reserved for future analytics / per-story logic
  character,  // reserved for future per-character timing tweaks
  audioRef,
  onNextScene,
  onStoryComplete,
  onSkipExercise, // held for caller; not invoked directly by this hook
  onSessionStop,  // optional — called instead of onStoryComplete when limit fires
}) {
  const settingsRef = useRef(null);
  settingsRef.current = getAutoplaysettings();

  const [isPlaying, setIsPlaying] = useState(() => settingsRef.current.enabled);
  const [resumeTrigger, setResumeTrigger] = useState(0);
  const [sessionStoriesPlayed, setSessionStoriesPlayed] = useState(0);
  const [sessionMinutesPlayed, setSessionMinutesPlayed] = useState(0);

  const sceneTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);
  const audioCleanupRef = useRef(null);

  const sessionStartMsRef = useRef(Date.now());
  const sessionStoriesRef = useRef(0);

  const onNextSceneRef = useRef(onNextScene);
  const onStoryCompleteRef = useRef(onStoryComplete);
  const onSessionStopRef = useRef(onSessionStop);
  onNextSceneRef.current = onNextScene;
  onStoryCompleteRef.current = onStoryComplete;
  onSessionStopRef.current = onSessionStop;

  const clearSceneTimer = useCallback(() => {
    clearTimeout(sceneTimerRef.current);
    sceneTimerRef.current = null;
    if (audioCleanupRef.current) {
      audioCleanupRef.current();
      audioCleanupRef.current = null;
    }
  }, []);

  const clearAll = useCallback(() => {
    clearSceneTimer();
    clearTimeout(resumeTimerRef.current);
  }, [clearSceneTimer]);

  useEffect(() => {
    const s = settingsRef.current;
    if (!s.enabled || !isPlaying || totalScenes === 0) return;

    clearSceneTimer();

    const isLast = currentIndex >= totalScenes - 1;
    const pauseMs = isLast ? s.pauseBetweenSeconds * 1000 : 0;

    const fireAction = () => {
      if (isLast) {
        const newCount = sessionStoriesRef.current + 1;
        sessionStoriesRef.current = newCount;
        setSessionStoriesPlayed(newCount);

        const elapsedMin = (Date.now() - sessionStartMsRef.current) / 60000;
        setSessionMinutesPlayed(Math.floor(elapsedMin));

        const hitStoryLimit =
          s.stopMode === 'stories' && newCount >= s.stopAfterStories;
        const hitTimeLimit =
          s.stopMode === 'minutes' && elapsedMin >= s.stopAfterMinutes;

        if (hitStoryLimit || hitTimeLimit) {
          setIsPlaying(false);
          onSessionStopRef.current?.();
          return;
        }

        onStoryCompleteRef.current?.();
      } else {
        onNextSceneRef.current?.();
      }
    };

    const useAudioTiming =
      Boolean(currentAudioUrl) &&
      (s.timingMode === 'audio' || s.timingMode === 'text');

    if (useAudioTiming) {
      const audioEl = audioRef?.current || document.querySelector('audio');

      if (audioEl) {
        scheduleAfterAudio({
          audioEl,
          expectedUrl: currentAudioUrl,
          textLength: currentTextLength,
          fireAction,
          pauseMs,
          sceneTimerRef,
          audioCleanupRef,
        });
      } else {
        const delay = Math.max(5000, currentTextLength * 100) + pauseMs;
        sceneTimerRef.current = setTimeout(fireAction, delay);
      }
    } else if (s.timingMode === 'text') {
      const delay = Math.max(5000, currentTextLength * 100) + pauseMs;
      sceneTimerRef.current = setTimeout(fireAction, delay);
    } else {
      sceneTimerRef.current = setTimeout(
        fireAction,
        s.fixedSeconds * 1000 + pauseMs,
      );
    }

    return clearSceneTimer;
  }, [
    currentIndex,
    totalScenes,
    currentAudioUrl,
    currentTextLength,
    isPlaying,
    resumeTrigger,
    audioRef,
    clearSceneTimer,
  ]);

  useEffect(() => {
    return clearAll;
  }, [clearAll]);

  const pauseAutoplay = useCallback(() => {
    clearSceneTimer();
    clearTimeout(resumeTimerRef.current);
    setIsPlaying(false);
    resumeTimerRef.current = setTimeout(() => setIsPlaying(true), 10_000);
  }, [clearSceneTimer]);

  const resumeAutoplay = useCallback(() => {
    clearTimeout(resumeTimerRef.current);
    setIsPlaying(true);
    setResumeTrigger((t) => t + 1);
  }, []);

  const stopAutoplay = useCallback(() => {
    clearAll();
    setIsPlaying(false);
  }, [clearAll]);

  return {
    isAutoplaying: settingsRef.current.enabled && isPlaying,
    sessionStoriesPlayed,
    sessionMinutesPlayed,
    pauseAutoplay,
    resumeAutoplay,
    stopAutoplay,
  };
}
